import { MdsButton } from "@/design-system/components/button";
import { MdsIcon } from "@/design-system/components/icon";
import { MdsIconKind } from "@/design-system/components/icon";
import { MdsButtonVariant } from "@/design-system/components/button";
import { CenteredFloatingActions } from "@/components/floating-actions/FloatingActions";
import { NoteSuggestionRefineActions } from "@/components/note/NoteSuggestionRefineActions";
import { MemCommonEditorStore } from "@/components/note/store/MemCommonEditorStore";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { css } from "@/domains/emotion";
import { mdsColors } from "@/design-system/foundations/colors";
import styled from "@emotion/styled";
import { NoteObservable } from "@/store/note/NoteObservable";

interface NoteSuggestionActionsProps {
  editorStore: MemCommonEditorStore;
  verticalOffset?: number;
}

export const NoteSuggestionActions = observer(function NoteSuggestionActions({
  editorStore,
  verticalOffset,
}: NoteSuggestionActionsProps) {
  const isRefineEnabled =
    editorStore.noteObservable instanceof NoteObservable &&
    !!editorStore.noteObservable.suggestionData;
  const [showRefineInput, setShowRefineInput] = useState(false);

  if (showRefineInput && editorStore.noteObservable instanceof NoteObservable) {
    return (
      <NoteSuggestionRefineActions
        noteObservable={editorStore.noteObservable}
        setShowRefineInput={setShowRefineInput}
        verticalOffset={verticalOffset}
      />
    );
  }

  return (
    <CenteredFloatingActions isOpen={true} verticalOffset={verticalOffset}>
      <StyledContainer>
        <MdsIcon kind={MdsIconKind.Pen} />
        <StyledText>Edited Note</StyledText>
        <MdsButton
          label="Reject"
          className={RejectButtonStyles}
          variant={MdsButtonVariant.Outlined}
          iconKind={MdsIconKind.Exit}
          onClick={() => editorStore.closeSuggestion(false)}
        />
        <MdsButton
          label="Refine"
          variant={MdsButtonVariant.Outlined}
          iconKind={MdsIconKind.Sparkle}
          isDisabled={!isRefineEnabled}
          onClick={() => setShowRefineInput(true)}
        />
        <MdsButton
          label="Accept"
          variant={MdsButtonVariant.FilledDark}
          iconKind={MdsIconKind.Check}
          onClick={() => editorStore.closeSuggestion(true)}
        />
      </StyledContainer>
    </CenteredFloatingActions>
  );
});

const RejectButtonStyles = css({
  color: mdsColors().red.x500,
  "& .mds-btn-icon": {
    color: mdsColors().red.x500,
  },
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey.x25};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  box-shadow: 0px 6px 10px rgba(69, 78, 104, 0.2);
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-start;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.fontSizes.small};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StyledText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.grey.x600};
  margin-right: ${({ theme }) => theme.spacing.sm};
`;
